import React from 'react';
import {
  Typography,
  Box,
  IconButton,
  Checkbox,
  Paper,
  Button
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DataTable, { ColumnDefinition, SortConfig } from '../common/DataTable';
import { Simulation } from '../../hooks/useSimulations';
import { FancySkeleton } from '../common/FancySkeleton';
import { calcReputationDelta, getReputationLevel } from '../../utils/reputationUtils';
import SimulationCard from './SimulationCard';

interface SimulationsListProps {
  simulations: Simulation[];
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNewSimulation: () => void;
}

const SimulationsList: React.FC<SimulationsListProps> = ({
  simulations,
  sortConfig,
  onSort,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onNewSimulation
}) => {
  const navigate = useNavigate();

  // Get reputation impact for a simulation (now using backend calculation)
  const getReputationImpact = (sim: Simulation) => {
    const forecast = sim.resultJson?.data?.forecast;
    if (!forecast) return { level: 'Unknown', color: '#999', score: 0 };
    
    // First check if we have the new reputation data from backend
    if (forecast.reputation) {
      return {
        level: forecast.reputation.level,
        color: forecast.reputation.color,
        score: Math.round(forecast.reputation.repDelta)
      };
    }
    
    // Fallback to frontend calculation for older simulations
    const { repDelta, uiScore } = calcReputationDelta({
      positivePct: forecast.positivePct || 0,
      negativePct: forecast.negativePct || 0,
      confidence: forecast.confidence || 'Low',
      delta: forecast.delta
    });
    
    const levelInfo = getReputationLevel(repDelta);
    return { ...levelInfo, score: Math.round(repDelta) };
  };

  // Get confidence level with color
  const getConfidenceLevel = (sim: Simulation) => {
    const confidence = sim.resultJson?.data?.forecast?.confidence || 'Low';
    
    switch (confidence) {
      case 'High':
        return { level: 'High', color: '#06D6A0' }; // Green
      case 'Medium':
        return { level: 'Medium', color: '#FFD166' }; // Yellow
      case 'Low':
      default:
        return { level: 'Low', color: '#FF6B6B' }; // Red
    }
  };

  // Handle row click to navigate to simulation details
  const handleRowClick = (simulation: Simulation) => {
    navigate(`/simulations/${simulation.id}/results`);
  };

  // Column configuration for DataTable
  const columns: ColumnDefinition<Simulation>[] = [
    {
      key: 'eventType',
      label: 'Event Type',
      sortKey: 'resultJson.data.forecast.eventType',
      render: (sim) => {
        const eventTypeRaw = sim.resultJson?.data?.forecast?.eventType || 'unknown';
        const eventType = eventTypeRaw.charAt(0).toUpperCase() + eventTypeRaw.slice(1);
        return <Typography>{eventType}</Typography>;
      }
    },
    {
      key: 'platform',
      label: 'Platform',
      render: (sim) => <Typography>{sim.platform || 'Unknown platform'}</Typography>
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (sim) => <Typography>{new Date(sim.createdAt).toLocaleString()}</Typography>
    },
    {
      key: 'createdBy',
      label: 'Created By',
      sortable: false,
      render: (sim) => {
        let creatorName = 'Unknown';
        if (sim.user) {
          if (sim.user.firstName && sim.user.lastName) {
            creatorName = `${sim.user.firstName} ${sim.user.lastName}`;
          } else if (sim.user.firstName) {
            creatorName = sim.user.firstName;
          } else if (sim.user.lastName) {
            creatorName = sim.user.lastName;
          } else if (sim.user.email) {
            creatorName = sim.user.email;
          }
        }
        return <Typography>{creatorName}</Typography>;
      }
    },
    {
      key: 'sender',
      label: 'Account',
      render: (sim) => <Typography>{sim.sender || 'Unknown'}</Typography>
    },
    {
      key: 'sector',
      label: 'Sector',
      render: (sim) => <Typography>{sim.sector || 'Unknown'}</Typography>
    },
    {
      key: 'reputationImpact',
      label: 'Reputation Impact',
      sortKey: 'resultJson.data.forecast.repDelta',
      render: (sim) => {
        const hasForecast = !!sim.resultJson?.data?.forecast;
        if (!hasForecast) {
          return <FancySkeleton height={18} width={120} radius={10} />;
        }
        const reputationInfo = getReputationImpact(sim);
        return (
          <Typography sx={{ color: reputationInfo.color, fontWeight: 'medium' }}>
            {reputationInfo.level}
          </Typography>
        );
      }
    },
    {
      key: 'confidence',
      label: 'Confidence',
      sortKey: 'resultJson.data.forecast.confidence',
      render: (sim) => {
        const hasForecast = !!sim.resultJson?.data?.forecast;
        if (!hasForecast) {
          return <FancySkeleton height={18} width={80} radius={10} />;
        }
        const confidenceInfo = getConfidenceLevel(sim);
        return (
          <Typography sx={{ color: confidenceInfo.color, fontWeight: 'medium' }}>
            {confidenceInfo.level}
          </Typography>
        );
      }
    },
    {
      key: 'hasImage',
      label: 'Includes Image',
      align: 'center',
      render: (sim) => (
        <Checkbox 
          checked={sim.hasImage ?? false}
          disabled
        />
      )
    },
    {
      key: 'isPrivate',
      label: 'Is Private',
      align: 'center',
      render: (sim) => (
        <Checkbox 
          checked={sim.isPrivate ?? false}
          disabled
        />
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      render: (sim) => (
        <IconButton 
          aria-label="view results" 
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click event
            navigate(`/simulations/${sim.id}/results`);
          }}
          size="small"
        >
          <VisibilityIcon />
        </IconButton>
      )
    }
  ];

  // Mobile sort options
  const mobileSortOptions = [
    { key: 'createdAt', label: 'Created Date' },
    { key: 'resultJson.data.forecast.eventType', label: 'Event Type' },
    { key: 'platform', label: 'Platform' },
    { key: 'sender', label: 'Account' },
    { key: 'sector', label: 'Sector' },
    { key: 'resultJson.data.forecast.repDelta', label: 'Reputation Impact' },
    { key: 'resultJson.data.forecast.confidence', label: 'Confidence' }
  ];

  // Mobile card renderer
  const renderMobileCard = (simulation: Simulation) => (
    <SimulationCard
      key={simulation.id}
      simulation={simulation}
      onView={(id) => navigate(`/simulations/${id}/results`)}
      getReputationImpact={getReputationImpact}
      getConfidenceLevel={getConfidenceLevel}
    />
  );

  // Empty state component for when there are no simulations
  const emptyStateComponent = (
    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
      <AddIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'medium' }}>
        No Simulations Yet
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
        Get started by running your first simulation. Analyze how your content will perform 
        and predict audience reactions before you post.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        size="large"
        startIcon={<AddIcon />}
        onClick={onNewSimulation}
        sx={{ mt: 1 }}
      >
        Run Your First Simulation
      </Button>
    </Paper>
  );

  return (
    <Box component="section" aria-labelledby="simulations-content-heading">
      <Typography 
        component="h2" 
        id="simulations-content-heading"
        sx={{ 
          border: 0,
          clip: 'rect(0 0 0 0)',
          height: '1px',
          margin: '-1px',
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          width: '1px',
          whiteSpace: 'nowrap', 
        }}
      >
        Simulations List
      </Typography>

      <DataTable
        data={simulations}
        columns={columns}
        sortConfig={sortConfig}
        onSort={onSort}
        onRowClick={handleRowClick}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        renderMobileCard={renderMobileCard}
        mobileSortOptions={mobileSortOptions}
        tableAriaLabel="simulations table"
        emptyStateComponent={emptyStateComponent}
      />
    </Box>
  );
};

export default SimulationsList;
